import { useGameStateApi } from './api/useGameStateApi'
import { useUserApi } from './api/useUserApi'
import { useProjectApi } from './api/useProjectApi'
import { useEventApi } from './api/useEventApi'
import { useWorkApi } from './api/useWorkApi'
import { useCommerceApi } from './api/useCommerceApi'
import { useConsultApi } from './api/useConsultApi'
import { useGithubApi } from './api/useGithubApi'
import { useTaskApi } from './api/useTaskApi'

// Re-export all types from domain API composables for backward compatibility
export type { GameNotification, GetGameStateResponse, GetGameNotificationsResponse, SkillCard, ListSkillsResponse } from './api/useGameStateApi'
export type { CreateChildResponse, ChildInfo, ListChildrenResponse, UpdateUserAttributeRequest, UpdateUserAttributeResponse, ExternalLinks, CreatorPublic } from './api/useUserApi'
export type { CheckCodeResponse, ListProjectItem, ListProjectsResponse, ProjectFile, GetProjectResponse, GetProjectFileResponse, UpdateProjectFileResponse, CreateEventTriggerResponse, AdvisorMenuInfo, AdvisorMenuOption, AdvisorSubOption, ProactiveHint, ProactiveHintSignals, QuillResult, QuillEdit, QuillClarification, CreateQuillResponse } from './api/useProjectApi'
export type { CreateUserEventRequest, CreateUserEventResponse } from './api/useEventApi'
export type { Work, WorkVisibility, WorksSortKey, ListWorksResponse, CreateSessionResponse, GenerateSlugResponse, LikeWorkResponse, FavoriteWorkResponse, CoverUploadTarget, CoverUploadsResponse, CoverImageKeys } from './api/useWorkApi'
export type { Product, ListProductsResponse, FetchClientSecretRequest, CreatePurchaseResponse } from './api/useCommerceApi'
export type { CreateConsultRequest, CreateConsultResponse } from './api/useConsultApi'
export type { GithubAuthStartResponse, GithubAuthCallbackResponse, GithubAuthDisconnectResponse, GithubRepo, ListGithubReposResponse, GithubTreeEntry, GetGithubRepoTreeResponse, GetGithubRepoReadmeResponse } from './api/useGithubApi'

// Keep ListWorksRequest here (not used in any composable but was exported)
export interface ListWorksRequest {
  page?: number
  perPage?: number
}

/**
 * Facade composable that re-exports all domain API methods.
 * Existing callers can continue using `useApi()` without changes.
 */
export function useApi() {
  const { getGameState, getGameNotifications, listSkills } = useGameStateApi()
  const { createChild, listChildren, updateChildSettings, updateUserAttribute, getPublicCreator, listCreatorWorks } = useUserApi()
  const {
    listProjects, getProject, getProjectFile, updateProjectFile,
    createAdvice, createQuill, applyQuill, copiedQuill, listHistory, submitUserRating, checkCurrentCode, listAdvisors, getProactiveHint,
    renameProject, deleteProject,
    createProjectFile, deleteProjectFile, renameProjectFile,
    duplicateProjectFile, getProjectFileLineage,
  } = useProjectApi()
  const { triggerEvent, tryUnlockScene, redeemGiftKey } = useEventApi()
  const { listWorks, getPublicWork, listPublicWorks, listMyWorks, updateWorkVisibility, getWork, likeWork, unlikeWork, reportWork, favoriteWork, unfavoriteWork, decodeWork, createProjectSession, createWorkSession, createPublicSession, createSessionClose, generateSlug, uploadCoverImages } = useWorkApi()
  const { listProducts, createCheckoutSession, cancelCheckoutSession } = useCommerceApi()
  const { createConsult } = useConsultApi()
  const { getGithubAuthUrl, exchangeGithubCode, disconnectGithub, listGithubRepos, getGithubRepoTree, getGithubRepoReadme, createGithubSession } = useGithubApi()
  const { getTask, forceTaskState } = useTaskApi()

  return {
    // GameState
    getGameState,
    getGameNotifications,
    // スキルのカタログ (#933)
    listSkills,
    // Children
    createChild,
    listChildren,
    updateChildSettings,
    // User Attributes
    tryUnlockScene,
    redeemGiftKey,
    updateUserAttribute,
    // Public Creators (#417)
    getPublicCreator,
    listCreatorWorks,
    // Projects
    listProjects,
    getProject,
    getProjectFile,
    updateProjectFile,
    renameProject,
    deleteProject,
    createProjectFile,
    deleteProjectFile,
    renameProjectFile,
    duplicateProjectFile,
    getProjectFileLineage,
    // Advisor
    listAdvisors,
    getProactiveHint,
    createAdvice,
    // 羽根筆 (#938)
    createQuill,
    applyQuill,
    copiedQuill,
    listHistory,
    // User Rating (#654)
    submitUserRating,
    // Consult (Owl Mail)
    createConsult,
    // Events
    triggerEvent,
    // Works
    listWorks,
    getWork,
    listMyWorks,
    updateWorkVisibility,
    likeWork,
    unlikeWork,
    reportWork,
    favoriteWork,
    unfavoriteWork,
    decodeWork,
    // My Codes
    checkCurrentCode,
    // Public Works
    getPublicWork,
    listPublicWorks,
    createPublicSession,
    // Sessions
    createProjectSession,
    createWorkSession,
    createSessionClose,
    // Slug Generation
    generateSlug,
    uploadCoverImages,
    // Products
    listProducts,
    // Checkout
    createCheckoutSession,
    cancelCheckoutSession,
    // GitHub OAuth
    getGithubAuthUrl,
    exchangeGithubCode,
    disconnectGithub,
    // GitHub Repos
    listGithubRepos,
    getGithubRepoTree,
    getGithubRepoReadme,
    // GitHub Crystallize
    createGithubSession,
    // Task API (#791 MR6)
    getTask,
    forceTaskState,
  }
}
